import React, { useState, useCallback, useRef, useEffect } from "react";
import type { HexCoord, HexGrid, CascadeAnimationState } from "../types.ts";
import { useCanvas } from "../hooks/use-canvas.ts";
import { drawHexGrid } from "../canvas/hex-renderer.ts";
import { detectHexHit } from "../canvas/hit-detection.ts";

interface GameBoardProps {
  grid: HexGrid;
  cascadeAnimation: CascadeAnimationState | null;
  onDrop: (coord: HexCoord) => void;
  onCascadeTick: () => void;
  disabled: boolean;
}

export function GameBoard({ grid, cascadeAnimation, onDrop, onCascadeTick, disabled }: GameBoardProps) {
  const [hovered, setHovered] = useState<HexCoord | null>(null);
  const sizeRef = useRef({ width: 0, height: 0 });

  const isAnimating = !!cascadeAnimation;

  // Show animation frame or final grid
  const displayGrid = cascadeAnimation
    ? (cascadeAnimation.frames[cascadeAnimation.currentFrame] ?? grid)
    : grid;

  // Highlight info for current cascade step
  const overflowHighlight = cascadeAnimation
    ? cascadeAnimation.overflows[cascadeAnimation.currentFrame] ?? null
    : null;

  const canvasRef = useCanvas(
    (ctx, width, height) => {
      sizeRef.current = { width, height };
      drawHexGrid(ctx, displayGrid, width, height, {
        hoveredHex: isAnimating ? null : hovered,
        overflowHighlight,
      });
    },
    [displayGrid, hovered, overflowHighlight, isAnimating]
  );

  // Advance cascade animation frames with timer
  useEffect(() => {
    if (!cascadeAnimation) return;
    const isFirstFrame = cascadeAnimation.currentFrame === 0;
    const delay = isFirstFrame ? 250 : 150;
    const timer = setTimeout(onCascadeTick, delay);
    return () => clearTimeout(timer);
  }, [cascadeAnimation?.currentFrame, cascadeAnimation, onCascadeTick]);

  const getMousePos = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },
    []
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (disabled || isAnimating) return;
      const pos = getMousePos(e);
      if (!pos) return;
      const hit = detectHexHit(
        pos.x,
        pos.y,
        grid,
        sizeRef.current.width,
        sizeRef.current.height
      );
      setHovered(hit);
    },
    [grid, disabled, isAnimating, getMousePos]
  );

  const handleMouseLeave = useCallback(() => {
    setHovered(null);
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (disabled || isAnimating) return;
      const pos = getMousePos(e);
      if (!pos) return;
      const hit = detectHexHit(
        pos.x,
        pos.y,
        grid,
        sizeRef.current.width,
        sizeRef.current.height
      );
      if (hit) {
        onDrop(hit);
      }
    },
    [grid, disabled, isAnimating, getMousePos, onDrop]
  );

  return (
    <canvas
      ref={canvasRef}
      className="w-full aspect-square max-w-[700px] cursor-pointer"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    />
  );
}
